import api from './baseApi';
import type {
  HandlingsCoachJobResult,
  HandlingsCoachV2Request,
  HandlingsCoachV2Result,
  HandlingsCoachV2Section,
  OtHDevelopmentPlanResponse,
  OtHOverallSummaryOnlyEnvelope,
} from 'src/contracts/oth';

export type DevelopmentPlanMidId = 'now' | 'desired' | 'actions';

export type DevelopmentPlanTopId =
  | 'ambisjonsprosess'
  | 'lagorganisering'
  | 'prestasjonskultur'
  | 'ledelsePersonlig';

export type DevelopmentPlanSubId =
  | 'visjon'
  | 'misjon'
  | 'delmaal'
  | 'hundreAnsvar'
  | 'samspillsoner'
  | 'verdier'
  | 'feedback'
  | 'utfordringer'
  | 'coaching';

export type DevelopmentPlanDataDto = Record<
  DevelopmentPlanMidId,
  Record<DevelopmentPlanTopId, Record<DevelopmentPlanSubId, string>>
>;

export interface DevelopmentPlanDto {
  _id: string;
  user_id: string;
  version: number;
  data: DevelopmentPlanDataDto;
  /** Stikkord for one-pager (samme struktur som data). */
  keywords?: DevelopmentPlanDataDto;
  createdAt: string;
  updatedAt: string;
  /** Cached AI handlingscoach sections (boxId -> section), only when input matches. */
  ai_results?: Record<string, HandlingsCoachV2Section>;
}

export interface UpsertDevelopmentPlanRequest {
  version: number;
  data: DevelopmentPlanDataDto;
  /** Optional: stikkord for one-pager. */
  keywords?: DevelopmentPlanDataDto;
}

export type DevelopmentPlanBoxId = string;

export interface EvaluateDevelopmentPlanAiBoxesRequest {
  /**
   * Optional override payload (same shape as DevelopmentPlanDto.data).
   * If omitted, backend will use saved plan for current user.
   *
   * Note: providing `data` may disable backend caching (depends on backend implementation).
   */
  data?: DevelopmentPlanDataDto;
  /**
   * Stable box IDs to request, typically `${topId}:${subId}`.
   */
  boxIds?: DevelopmentPlanBoxId[];
  /**
   * Backend supported scopes.
   * - `requested_only`: only the requested `boxIds`
   * - `all_available`: all boxes in the plan
   */
  scope?: 'requested_only' | 'all_available';
}

export const developmentPlanApi = {
  async upsertPlan(
    body: UpsertDevelopmentPlanRequest
  ): Promise<DevelopmentPlanDto> {
    const response = await api.put(`/oth/development-plan`, body);
    return response.data.result;
  },

  async getMyPlan(): Promise<DevelopmentPlanDto | null> {
    const response = await api.get(`/oth/development-plan`);
    return response.data.result ?? null;
  },

  async getAiResults(): Promise<Record<string, HandlingsCoachV2Section>> {
    const response = await api.get(`/oth/development-plan/ai-results`);
    return (response.data.result ?? {}) as Record<
      string,
      HandlingsCoachV2Section
    >;
  },

  async generateAiTiltak(input?: {
    /**
     * Optional override payload (same shape as DevelopmentPlanDto.data).
     * If omitted, backend will use saved plan.
     */
    data?: DevelopmentPlanDataDto;
  }): Promise<unknown> {
    const response = await api.post(`/oth/development-plan/ai-tiltak`, {
      ...(input?.data ? { data: input.data } : {}),
    });
    return response.data.result;
  },

  async evaluateAiBoxes(
    body: EvaluateDevelopmentPlanAiBoxesRequest
  ): Promise<OtHDevelopmentPlanResponse> {
    // Backend may return merged (cached + newly generated) results.
    const response = await api.post(`/oth/development-plan/ai-tiltak`, body);
    return response.data.result as OtHDevelopmentPlanResponse;
  },

  async overallSummaryOnly(input: {
    scope: 'requested_only' | 'all';
    boxIds?: string[];
  }): Promise<OtHOverallSummaryOnlyEnvelope> {
    const response = await api.post(
      `/oth/development-plan/ai-overall-summary`,
      input
    );
    return response.data as OtHOverallSummaryOnlyEnvelope;
  },
  /**
   * AI HandlingsCoach V2 – genererer observasjoner, refleksjoner og foreslåtte tiltak
   * per seksjon (Visjon/Misjon/Delmål m.m.).
   *
   * Legacy sync HandlingsCoach V2. New analysis flow uses startHandlingsCoachJob.
   *
   * Auth: som andre dev-plan endepunkter.
   * Prod/staging: userRequired.
   * Dev: noUserRequired kun hvis data override sendes (ellers 403).
   */
  async aiHandlingsCoachV2(
    body?: HandlingsCoachV2Request
  ): Promise<HandlingsCoachV2Result> {
    const response = await api.post(
      `/oth/development-plan/ai-handlingscoach-v2`,
      body ?? {}
    );
    return response.data.result as HandlingsCoachV2Result;
  },

  /**
   * Starts (or reuses) an async HandlingsCoach job for exactly one box.
   * 200: completed / cache hit. 202: queued or already running.
   */
  async startHandlingsCoachJob(
    body: HandlingsCoachV2Request
  ): Promise<HandlingsCoachJobResult> {
    const response = await api.post(
      `/oth/development-plan/ai-handlingscoach-v2/jobs`,
      body
    );
    return response.data.result as HandlingsCoachJobResult;
  },

  async getHandlingsCoachJob(jobId: string): Promise<HandlingsCoachJobResult> {
    const response = await api.get(
      `/oth/development-plan/ai-handlingscoach-v2/jobs/${encodeURIComponent(jobId)}`
    );
    return response.data.result as HandlingsCoachJobResult;
  },

  /**
   * Generates short keywords (stikkord) for one-pager from source texts.
   * Only send items where source has text and keyword cell is empty.
   */
  async generateKeywords(body: {
    items: Array<{ id: string; sourceText: string }>;
  }): Promise<{
    keyword_items: Array<{ id: string; keyword: string }>;
    meta: { requestId: string; model: string | null; generated_at: string };
  }> {
    const response = await api.post(`/oth/development-plan/ai-keywords`, body);
    return response.data.result;
  },
};
